import { ReactNode, useCallback, useEffect, useRef } from "react"

import { IconGripHorizontal, IconX } from "@tabler/icons-react"
import { Resizable, ResizableProps } from "re-resizable"

import { Button } from "@/components/ui"
import { cn } from "@/lib/utils"

export const DraggablePIP = ({
  className,
  onClose,
  onClick,
  minHeight,
  minWidth,
  maxHeight,
  maxWidth,
  defaultSize = { width: 400, height: 400 },
  children,
}: {
  className?: string
  defaultSize?: ResizableProps["defaultSize"]
  minWidth?: ResizableProps["minWidth"]
  minHeight?: ResizableProps["minHeight"]
  maxWidth?: ResizableProps["maxWidth"]
  maxHeight?: ResizableProps["maxHeight"]
  onClose: () => void
  onClick?: () => void
  children: ReactNode
}) => {
  const ref = useRef<HTMLDivElement>(null)
  const handleRef = useRef<HTMLDivElement>(null)
  const pos = useRef<{ newX: number; newY: number; startX: number; startY: number }>({
    newX: 0,
    newY: 0,
    startX: 0,
    startY: 0,
  })
  const resizing = useRef(false)

  const onMouseMove = useCallback((e: MouseEvent) => {
    e.preventDefault()
    if (resizing.current) return

    pos.current.newX = pos.current.startX - e.clientX
    pos.current.newY = pos.current.startY - e.clientY
    pos.current.startX = e.clientX
    pos.current.startY = e.clientY

    const el = ref.current
    if (el && !resizing.current) {
      el.style.top = el.offsetTop - pos.current.newY + "px"
      el.style.left = el.offsetLeft - pos.current.newX + "px"
    }
  }, [])

  const onMouseUp = useCallback(
    (e: MouseEvent) => {
      e.preventDefault()
      document.removeEventListener("mouseup", onMouseUp)
      document.removeEventListener("mousemove", onMouseMove)
    },
    [onMouseMove],
  )

  const onMouseDown = useCallback(
    (e: MouseEvent) => {
      pos.current.startX = e.clientX
      pos.current.startY = e.clientY
      e.preventDefault()
      onClick?.()

      document.addEventListener("mouseup", onMouseUp)
      document.addEventListener("mousemove", onMouseMove)
    },
    [onMouseMove, onMouseUp, onClick],
  )

  useEffect(() => {
    const el = handleRef.current
    el?.addEventListener("mousedown", onMouseDown)
    return () => {
      el?.removeEventListener("mousedown", onMouseDown)
    }
  }, [onMouseDown])

  return (
    <div
      ref={ref}
      className={cn(
        "fixed top-0 left-0 z-[200] rounded-md border-2 border-border bg-muted shadow-md",
        className,
      )}
      onClick={() => onClick?.()}
    >
      <div onMouseDown={(e) => e.stopPropagation()}>
        <Resizable
          onResize={(_e, dir, _el, delta) => {
            if (dir === "topRight" || dir === "top" || dir === "topLeft") {
              pos.current.newY = pos.current.startY - delta.height
              const el = ref.current
              if (el) {
                el.style.top = pos.current.newY + "px"
              }
            }
            if (dir === "topLeft" || dir === "left" || dir === "bottomLeft") {
              pos.current.newX = pos.current.startX - delta.width
              const el = ref.current
              if (el) {
                el.style.left = pos.current.newX + "px"
              }
            }
          }}
          onResizeStart={() => (resizing.current = true)}
          onResizeStop={() => (resizing.current = false)}
          defaultSize={defaultSize}
          minWidth={minWidth}
          minHeight={minHeight}
          maxWidth={maxWidth}
          maxHeight={maxHeight}
          className="p-2"
        >
          <div className="flex h-full w-full flex-col overflow-auto">
            <div
              ref={handleRef}
              className="flex cursor-grab items-center justify-center pt-1 pb-2"
            >
              <IconGripHorizontal size={24} />
            </div>
            <Button
              icon={IconX}
              iconOnly
              variant="neutral"
              onClick={onClose}
              noBorder
              className="absolute top-2 right-2"
            />

            {children}
          </div>
        </Resizable>
      </div>
    </div>
  )
}
